import styled from 'styled-components';
import { FrostedGlassStrong } from '@seed-health/tokens';
import { mqLargeMin, mqSmallMin } from 'styled/tools/media-queries';
import { imageUrl } from 'components/pdp/modules/helpers';
import type { SustainabilityModule } from 'components/pdp/modules/types';

const SustainWrapper = styled.div`
  margin: auto;
  padding: 5rem 1rem;
  max-width: 1440px;

  @media ${mqLargeMin} {
    padding: 5rem;
  }

  * {
    color: var(--color-primar-seed-green);
  }
`;

const Title = styled.h2`
  margin-bottom: 3rem;
  font-size: 32px;
  font-weight: 400;
  letter-spacing: -0.64px;
  line-height: 35.2px;
  text-wrap: auto;

  @media ${mqLargeMin} {
    font-size: 48px;
    letter-spacing: -0.96px;
    line-height: 52.8px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  gap: 1rem;
  letter-spacing: -0.16px;

  @media ${mqSmallMin} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CardItem = styled.div`
  position: relative;
  margin-bottom: 0.5rem;
  border-radius: 2rem;

  @media ${mqSmallMin} {
    padding: 1rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    background-color: var(--fill-secondary);
  }
`;

const CardContents = styled.div`
  position: absolute;
  left: 1rem;
  bottom: 1rem;
  right: 1rem;
  border-radius: 16px;
  padding: 1rem;
  letter-spacing: -0.16px;
  background-color: var(--color-neutral-frosted-glass-t35);
  backdrop-filter: ${FrostedGlassStrong};
  -webkit-backdrop-filter: ${FrostedGlassStrong};

  * {
    color: var(--fill-bright);
  }

  @media ${mqSmallMin} {
    position: static;
    border-radius: 0px;
    background-color: transparent;
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);

    * {
      color: var(--fill-brand);
    }
  }
`;

const MainImage = styled.picture`
  border-radius: 1rem;
  width: 100%;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
  }
`;

const CardTitle = styled.p`
  font-size: 1.5rem;
  font-weight: 400;
  letter-spacing: -0.16px;
`;

const CardDescription = styled.p`
  letter-spacing: -0.16px;
`;

const CardCallout = styled.div`
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:first-of-type {
    margin-top: 1rem;
  }

  &:not(:first-of-type) {
    border-top: 1px solid var(--color-neutral-faded-green-20);
  }

  img {
    display: none;
    max-width: 37px;
  }

  p:first-of-type {
    font-weight: 500;
    line-height: 1.5rem;
    letter-spacing: -0.16px;
  }

  p:last-child {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    opacity: 0.6;
    letter-spacing: -0.16px;
  }

  @media ${mqSmallMin} {
    img {
      display: block;
    }
  }
`;

const SustainableDelivery = ({
  sustainabilityModule,
}: {
  sustainabilityModule?: SustainabilityModule;
}) => {
  if (!sustainabilityModule?.cards?.length) {
    return null;
  }

  try {
    return (
      <SustainWrapper>
        <Title>Sustainably delivered, monthly.</Title>
        <CardGrid>
          {sustainabilityModule.cards.map((card) => {
            return (
              <CardItem key={card._key}>
                <MainImage>
                  <source
                    srcset={imageUrl(card.image, 'medium')}
                    media='(width >= 30em)'
                  />
                  <img
                    src={imageUrl(card.mobileImage, 'medium')}
                    alt={card.mobileImage.alt ?? ''}
                  />
                </MainImage>
                <CardContents>
                  <CardTitle>{card.title}</CardTitle>
                  <CardDescription>{card.content}</CardDescription>
                  {card.callouts.map((callout, calloutIndex) => {
                    return (
                      <CardCallout
                        key={`sustain-icon-${card._key}-${calloutIndex}`}
                      >
                        <img
                          src={imageUrl(callout.icon, 'thumbnail')}
                          alt={callout.icon.alt ?? ''}
                        />
                        <div>
                          <p>{callout.title}</p>
                          <p>{callout.description}</p>
                        </div>
                      </CardCallout>
                    );
                  })}
                </CardContents>
              </CardItem>
            );
          })}
        </CardGrid>
      </SustainWrapper>
    );
  } catch {
    return null;
  }
};

export default SustainableDelivery;
